import { makeAutoObservable, toJS } from 'mobx'
import { isIOS } from 'react-device-detect'

import { PIXEL_RATIO } from '../../constants'
import type { Point, Size, TimeStamp } from '../../types'
import type { NoteData } from '../handwrittenNote.types'
import HandwrittenNoteControllerService from './HandwrittenNoteController.service'
import HandwrittenNoteData from './HandwrittenNoteData'
import { PathType } from './HandwrittenNotePathItem'

const USE_SUBPIXEL = !isIOS
const PATH_TYPE = isIOS ? PathType.NoSubPixel : PathType.SubPixel

interface PanPointData {
  clientX: number
  clientY: number
  offsetX: number
  offsetY: number
}

export default class HandwrittenNoteService {
  static VERSION = 1

  isSaving = false
  eraserPoint?: Point = undefined
  writerFunc?: (data: NoteData) => Promise<void>
  noteData: HandwrittenNoteData = new HandwrittenNoteData()
  controllerService: HandwrittenNoteControllerService
  isHiddenNote = false
  activeTextBoxId: number | null = null
  serverDataTimestamp: TimeStamp = 0

  private lastSaveTimestamp: TimeStamp = 0
  private saveTimer = 0
  private viewBoxSize: Size | undefined
  private lastPointX: number | undefined
  private lastPointY: number | undefined

  constructor(controllerService?: HandwrittenNoteControllerService) {
    makeAutoObservable(this)
    if (!controllerService) {
      this.controllerService = new HandwrittenNoteControllerService()
    } else {
      this.controllerService = controllerService
    }
  }

  setIsSaving(value: boolean) {
    this.isSaving = value
  }

  setWriterFunc(value: (data: NoteData) => Promise<void>) {
    this.writerFunc = value
  }

  setEraserPoint(value: Point | undefined) {
    this.eraserPoint = value
  }

  setViewBoxSize(value: Size | undefined) {
    this.viewBoxSize = value
  }

  handlePanStart(data: PanPointData) {
    const { clientX, clientY, offsetX, offsetY } = data
    const { activeTool, toolColor, toolWidth } = this.controllerService

    if (activeTool === 'eraser') {
      this.noteData.addEraserItem({ x: clientX, y: clientY })
      this.setEraserPoint({ x: offsetX, y: offsetY })
    } else if (['pen', 'highlight-pen'].includes(activeTool) && toolColor) {
      this.lastPointX = offsetX
      this.lastPointY = offsetY

      this.noteData.addPathItem(
        { x: offsetX, y: offsetY },
        toolWidth ?? 0,
        toolColor,
        PATH_TYPE,
        USE_SUBPIXEL,
        PIXEL_RATIO,
      )
    }
  }

  handlePan(data: PanPointData) {
    const { clientX, clientY, offsetX, offsetY } = data
    const { activeTool } = this.controllerService

    if (activeTool === 'eraser') {
      this.noteData.deletePaths({ x: clientX, y: clientY })
      this.setEraserPoint({ x: offsetX, y: offsetY })
    } else if (this.lastPointX !== offsetX || this.lastPointY !== offsetY) {
      this.noteData.addPoint({ x: offsetX, y: offsetY })
    }
  }

  handlePanEnd() {
    this.setEraserPoint(undefined)
    this.noteData.checkModified()
    this.noteData.setPathRect()
    this.noteData.handleInvalidPathItems()
  }

  handleTextClick(data: { clientX: number; clientY: number }) {
    const { clientX, clientY } = data
    const { textSize, toolColor } = this.controllerService

    if (!toolColor) {
      return
    }

    this.noteData.addTextItem({ x: clientX, y: clientY }, textSize, toolColor)

    const activeTextBoxId =
      this.noteData.visibleTextItems[this.noteData.visibleTextItems.length - 1].id
    this.setActiveTextBoxId(activeTextBoxId)

    this.trackAndSave()

    return activeTextBoxId
  }

  private updateLastSaveTimestamp() {
    this.lastSaveTimestamp = new Date().getTime()
  }

  get isModified() {
    return this.lastSaveTimestamp < this.noteData.lastModified
  }

  // get activeTextBox() {
  //   return this.noteData.visibleTextItems.find((item) => item.id === this.activeTextBoxId)
  // }

  async saveNote() {
    if (!this.writerFunc) {
      return false
    }

    if (!this.viewBoxSize || !this.viewBoxSize.width || !this.viewBoxSize.height) {
      throw new Error(`invalid viewBoxSize`)
    }

    const paths = toJS(this.noteData.toPathDataItems())
    const textItems = toJS(this.noteData.toTextDataItems())

    if (!paths.length && !textItems.length) {
      // 데이터가 없어도 저장
      // return false
    }

    const noteData: NoteData = {
      version: HandwrittenNoteService.VERSION,
      paths,
      textItems,
      viewBox: [this.viewBoxSize.width, this.viewBoxSize.height],
    }

    let isSuccess = false

    try {
      this.setIsSaving(true)
      await this.writerFunc(noteData)
      this.updateLastSaveTimestamp()
      isSuccess = true
    } catch (err) {
      isSuccess = false
    } finally {
      this.setIsSaving(false)
    }

    return isSuccess
  }

  requestSave(delay = 2 * 1000) {
    window.clearTimeout(this.saveTimer)
    return new Promise<void>((resolve, reject) => {
      this.saveTimer = window.setTimeout(async () => {
        try {
          await this.saveNote()
          resolve()
        } catch (err) {
          reject(err)
        }
      }, delay)
    })
  }

  trackAndSave(delay?: number) {
    this.noteData.updateLastModified()
    this.requestSave(delay)
  }

  setActiveTextBoxId(id: number | null) {
    this.activeTextBoxId = id
    if (id !== null && this.controllerService.activeTool === 'text') {
      this.controllerService.setToolbarMode('full')
    }
  }

  reset() {
    this.noteData.reset()
    this.lastSaveTimestamp = 0
    this.serverDataTimestamp = 0
  }

  setServerDataTimestamp(timestamp: TimeStamp) {
    this.serverDataTimestamp = timestamp
  }

  shouldAcceptServerData(serverTimestamp?: TimeStamp): boolean {
    if (!serverTimestamp) {
      return true
    }
    if (this.isSaving) {
      return false
    }
    if (this.isModified) {
      return serverTimestamp > this.noteData.lastModified
    }
    return true
  }
}
